import { Button, Column, Drawer, DropdownButton, Search, Table } from "@appkit4/react-components";
import { ConfirmationModal, LoaderWrapper, toastMessage } from "components/common/helpers";
import { FC, useState } from "react";
import { useParams } from "react-router-dom";
import { SapSystem, UserLevel } from "routes/analysis/types/analysis";
import { useQueryClient } from "@tanstack/react-query";
import { FetchAnalyses } from "routes/administration/queries/analysis";
import { FetchSystems, DeleteSystem } from "routes/administration/queries/system";
import { useCookies } from "react-cookie";
import SystemForm from "./SystemForm";
import { decodeToken } from "services/common";


const SystemView: FC = () => {
  const { clientId } = useParams();
  const [editItem, setEditItem] = useState<SapSystem>();
  const [deleteItem, setDeleteItem] = useState<SapSystem>();
  const [search, setSearch] = useState("");
  const [cookie] = useCookies();

  const queryClient = useQueryClient();

  const { data: systems, isPending: isSystemPending, error: errorSystem } = FetchSystems(clientId);

  const { data: analyses, isPending: isAnalysesPending, error: errorAnalyses } = FetchAnalyses(clientId);

  const { mutate: deleteSystem } = DeleteSystem(clientId);

  const handleDelete = async (system?: SapSystem) => {
    if (!system) return;
    deleteSystem(system, {
      onSuccess: () => {
        setDeleteItem(undefined);
        toastMessage("System deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["systems", clientId] });
        return true;
      },
      onError: () => {
        toastMessage("Unable to delete system", "error");
      }
    });
  }

  const checkSystemUse = (systemId?: string) => {
    return !analyses?.some(f => f.systemId === systemId);
  }
  const checkUserLevel = (levels: UserLevel[]) => {
    const jwtData = decodeToken(cookie["id_token"]);
    return jwtData?.userLevel && levels.includes(UserLevel[jwtData.userLevel as keyof typeof UserLevel]);
  }
  const customizeColumn = (row: SapSystem, field: string) => {
    switch (field) {
      case "actions":
        return checkUserLevel([UserLevel.PwCUser, UserLevel.ClientAdmin, UserLevel.PwCAdmin]) && (
          <DropdownButton
            compact
            splitButton
            kind="tertiary"
            data={[
              { label: "Edit", value: 1 },
              { label: "Delete system", value: 2 },
            ]}
            onSelect={async (value) => {
              switch (value) {
                case 1:
                  setEditItem(systems?.find(f => f.systemId === row.systemId));
                  break;
                case 2:
                  setDeleteItem(row);
                  break;
                default:
                  break;
              }
            }}
            onClick={async () => setEditItem(systems?.find(f => f.systemId === row.systemId))}
          >
            Edit
          </DropdownButton>
        );
      default:
        return row[field as keyof SapSystem];
    }
  }
  return (
    <LoaderWrapper loading={[isAnalysesPending, isSystemPending]} errors={[errorAnalyses, errorSystem]}>
      <h3>Systems</h3>
      {
        checkUserLevel([UserLevel.PwCAdmin]) && <p>A view for creating new systems and reviewing existing systems.</p>
      }
      <div className="flex items-center gap-4 mt-4 mb-4">
        <div className="shrink">
          <Button kind='primary' icon="icon-plus-outline" onClick={() => setEditItem({ systemId: "new", sapNickname: "", sapSystemName: "", sapClient: "" })} disabled={!checkUserLevel([UserLevel.ClientAdmin, UserLevel.PwCAdmin])}>New system</Button>
        </div>
        <div>
          <Search
            searchType={"secondary"}
            onChange={(value: string) => {
              setSearch(value);
            }}
            searchValue={search}
            className="list-filter"
          />
        </div>
      </div>
      <Table
        originalData={systems?.map(m => ({ ...m, inUse: checkSystemUse(m.systemId), actions: "" })) || []}
        hasTitle
        striped
      >
        <Column field="inUse" sortKey="inUse" renderCell={(row: SapSystem) => checkSystemUse(row.systemId) ? "No" : "Yes"}>In use</Column>
        <Column field="sapNickname" sortKey="sapNickname" renderCell={customizeColumn}>Nickname</Column>
        <Column field="sapSystemName" sortKey="sapSystemName" renderCell={customizeColumn}>System name</Column>
        <Column field="sapClient" sortKey="sapClient" renderCell={customizeColumn}>Client</Column>
        <Column field="actions" sortKey="actions" renderCell={customizeColumn}>Actions</Column>
      </Table>
      <ConfirmationModal
        visible={deleteItem !== undefined}
        title="Delete system"
        cancel={() => setDeleteItem(undefined)}
        confirm={async () => deleteItem && await handleDelete(deleteItem)}>
        {checkSystemUse(deleteItem?.systemId) ? <p>Are you sure you want to delete {deleteItem?.sapNickname}?</p> : <p>System is in use and cannot be deleted</p>}
      </ConfirmationModal>
      <Drawer
        initialFocus={false}
        mask={true}
        resizable={true}
        visible={editItem !== undefined}
        placement="right"
        title={editItem?.sapSystemName || "Add new system"}
        onClose={() => {
          setEditItem(undefined);
        }}
        closable={true}
      >
        {
          editItem !== undefined && <SystemForm
            systemData={editItem || {}}
            cancel={() => {
              setEditItem(undefined);
            }}
          />
        }
      </Drawer>
    </LoaderWrapper>
  )
}


export default SystemView;